import { RefreshCw, Search, Trash2 } from "lucide-react";
import { FormEvent, useEffect, useState } from "react";
import { api } from "../lib/api";
import { formatCurrency, statusTone } from "../lib/format";
import type { Order } from "../types";

export function Orders() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  async function load(q = query) {
    setLoading(true);
    setError("");
    try {
      const data = await api.orders(q);
      setOrders(data.orders);
    } catch (err) {
      setError(err instanceof Error ? err.message : "加载失败");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load("");
  }, []);

  async function handleSearch(event: FormEvent) {
    event.preventDefault();
    await load(query);
  }

  async function remove(id: string) {
    if (!window.confirm("确定删除这条订单吗？")) {
      return;
    }

    await api.deleteOrder(id);
    await load();
  }

  return (
    <section className="page-stack">
      <header className="page-header">
        <div>
          <p className="eyebrow">Orders</p>
          <h1>订单列表</h1>
        </div>
        <button className="secondary-button" onClick={() => load()} type="button">
          <RefreshCw size={17} aria-hidden />
          刷新
        </button>
      </header>

      <form className="toolbar" onSubmit={handleSearch}>
        <label className="search-box">
          <Search size={18} aria-hidden />
          <input
            onChange={(event) => setQuery(event.target.value)}
            placeholder="搜索客户、商品、型号、运单号"
            value={query}
          />
        </label>
        <button className="primary-button" type="submit">
          搜索
        </button>
      </form>

      {error ? <div className="notice danger">{error}</div> : null}
      {loading ? <div className="notice">加载订单中...</div> : null}

      <section className="panel">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>平台</th>
                <th>大类</th>
                <th>订单日期</th>
                <th>客户</th>
                <th>手机尾号</th>
                <th>商品</th>
                <th>型号</th>
                <th>金额</th>
                <th>付款</th>
                <th>物流</th>
                <th>运单号</th>
                <th>预计到达</th>
                <th>备注</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => (
                <tr key={order.id}>
                  <td>{order.platform}</td>
                  <td>{order.category}</td>
                  <td>{order.order_date}</td>
                  <td>{order.customer_name || "-"}</td>
                  <td>{order.phone_tail || "-"}</td>
                  <td>{order.product_name}</td>
                  <td>{order.model || "-"}</td>
                  <td>{formatCurrency(order.amount)}</td>
                  <td>
                    <span className={`badge ${statusTone(order.payment_status)}`}>
                      {order.payment_status || "未填写"}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${statusTone(order.shipping_status)}`}>
                      {order.shipping_status || "未填写"}
                    </span>
                  </td>
                  <td>{order.tracking_number || "-"}</td>
                  <td>{order.eta || "-"}</td>
                  <td className="remark-cell">{order.remark || "-"}</td>
                  <td>
                    <button
                      className="icon-button danger"
                      onClick={() => remove(order.id)}
                      title="删除订单"
                      type="button"
                    >
                      <Trash2 size={16} aria-hidden />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!loading && orders.length === 0 ? (
            <p className="empty table-empty">暂无订单</p>
          ) : null}
        </div>
      </section>
    </section>
  );
}
